import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  Info,
  Lock,
  Mail,
  Phone,
  Printer,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <EmailPageLayout
      title="Privacy Policy"
      description="How we handle your personal information and email communications"
    >
      <div className="relative bg-gradient-to-b from-primary/10 to-background py-8 overflow-hidden rounded-t-lg mb-6">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8ZW1haWwlMjBzZWN1cml0eXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Privacy policy background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-background"></div>
        </div>
        <div className="relative z-10 px-4 max-w-3xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              How we handle your personal information and email communications
            </p>
            <div className="flex items-center justify-center mt-4 text-sm text-muted-foreground">
              <Lock className="h-4 w-4 mr-2" />
              <span>
                Last updated:{" "}
                {new Date().toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-2 max-w-3xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Link
            to="/email"
            className="text-primary hover:text-primary/80 flex items-center text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back to Email Dashboard
          </Link>
          <Badge
            variant="outline"
            className="text-xs bg-primary/5 border-primary/20 text-primary"
          >
            Legal Document
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
            <Printer className="h-3.5 w-3.5" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Download PDF
          </Button>
        </div>
      </div>

      <Tabs defaultValue="email" className="w-full px-4 max-w-3xl mx-auto">
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm pt-4 pb-2 mb-6">
          <TabsList className="w-full justify-start overflow-x-auto flex p-1 rounded-lg bg-muted/50">
            <TabsTrigger
              value="email"
              className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Communications
            </TabsTrigger>
            <TabsTrigger
              value="data"
              className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Info className="h-4 w-4 mr-2" />
              Data Collection
            </TabsTrigger>
            <TabsTrigger
              value="rights"
              className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Your Rights
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Lock className="h-4 w-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="email" className="space-y-4">
          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/50 text-blue-800 dark:text-blue-300">
            <Mail className="h-4 w-4 text-blue-500" />
            <AlertTitle className="text-sm font-medium">
              Email Communications Policy
            </AlertTitle>
            <AlertDescription className="text-xs text-blue-700 dark:text-blue-400">
              This section explains how we handle email communications with our
              clients.
            </AlertDescription>
          </Alert>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <Mail className="h-4 w-4 mr-2 text-blue-500" />
                Types of Emails We Send
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 dark:text-gray-400">
                At Numberz Insight, we send the following types of emails to our
                clients
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <Badge className="mt-0.5 bg-blue-100 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                    1
                  </Badge>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Document Requests:
                    </span>{" "}
                    Emails requesting specific tax documents from clients to
                    complete their tax returns.
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <Badge className="mt-0.5 bg-blue-100 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                    2
                  </Badge>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Information Requests:
                    </span>{" "}
                    Emails requesting additional information to clarify tax
                    matters.
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <Badge className="mt-0.5 bg-blue-100 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                    3
                  </Badge>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Service Updates:
                    </span>{" "}
                    Emails providing updates about tax service progress.
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <Badge className="mt-0.5 bg-blue-100 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                    4
                  </Badge>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Marketing Communications:
                    </span>{" "}
                    Occasional emails about new services or features (only sent
                    with explicit consent).
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Email Consent and Management
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  We only send emails to clients who have explicitly requested
                  our services and provided their email address during the
                  onboarding process. All clients can manage their email
                  preferences through our Email Preferences Center.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Unsubscribe Options
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  Every email we send includes a clear and conspicuous
                  unsubscribe link in the footer. Clients can unsubscribe from
                  specific types of communications or all emails at any time. We
                  process all unsubscribe requests immediately.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Frequency and Content
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  Our emails are strictly transactional in nature, related to
                  the tax services requested by the client. We do not send bulk
                  marketing emails or purchase email lists. Each email is
                  individually crafted for the specific client's situation.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Email Authentication
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  All our emails are authenticated using SPF, DKIM, and DMARC to
                  ensure they are legitimate and not spoofed. This helps protect
                  our clients from phishing attempts and ensures our emails are
                  delivered reliably.
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <ShieldCheck className="h-4 w-4 mr-2 text-blue-500" />
                Compliance with Australian Regulations
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Our email practices comply with the Australian Spam Act 2003,
                which requires:
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4 pl-5 list-disc">
                <li>Consent before sending commercial electronic messages</li>
                <li>Clear identification of the sender</li>
                <li>A functional unsubscribe facility</li>
              </ul>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                We also comply with the Privacy Act 1988 and Australian Taxation
                Office (ATO) requirements regarding the handling of personal and
                tax information.
              </p>
            </CardContent>
            <CardFooter className="pt-2 pb-4 flex justify-center border-t border-gray-100 dark:border-gray-800">
              <Link to="/email/preferences">
                <Button className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                  <Mail className="h-4 w-4" />
                  Manage Your Email Preferences
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="data" className="space-y-4">
          <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900/50 text-green-800 dark:text-green-300">
            <Info className="h-4 w-4 text-green-500" />
            <AlertTitle className="text-sm font-medium">
              Data Collection and Usage
            </AlertTitle>
            <AlertDescription className="text-xs text-green-700 dark:text-green-400">
              Information about how we collect, use, and store your data.
            </AlertDescription>
          </Alert>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <Info className="h-4 w-4 mr-2 text-green-500" />
                Information We Collect
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 dark:text-gray-400">
                We collect the following types of information
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-green-100 hover:bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                      1
                    </Badge>
                    Personal Information
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Name, email address, phone number, address, date of birth,
                    occupation, and tax file number.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-green-100 hover:bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                      2
                    </Badge>
                    Tax Information
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Income details, deductions, tax documents, and other
                    information necessary for tax preparation.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-green-100 hover:bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                      3
                    </Badge>
                    Communication Records
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Records of emails, document requests, and other
                    communications related to your tax services.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-green-100 hover:bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                      4
                    </Badge>
                    Usage Data
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Information about how you interact with our website and
                    services.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 pl-5 list-disc">
                  <li>Providing tax preparation and advisory services</li>
                  <li>Communicating with you about your tax matters</li>
                  <li>Complying with legal and regulatory requirements</li>
                  <li>Improving our services and user experience</li>
                  <li>Protecting against fraud and unauthorized access</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Data Retention
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  We retain your personal information for as long as necessary
                  to fulfill the purposes outlined in this privacy policy,
                  unless a longer retention period is required by law. Tax
                  records are typically retained for 5-7 years in accordance
                  with ATO requirements.
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <ShieldCheck className="h-4 w-4 mr-2 text-green-500" />
                Third-Party Sharing
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 dark:text-gray-400">
                We do not sell your personal information to third parties
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                We may share your information with:
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4 pl-5 list-disc">
                <li>
                  The Australian Taxation Office and other government agencies
                  as required by law
                </li>
                <li>
                  Service providers who assist us in operating our website and
                  services (all bound by confidentiality agreements)
                </li>
                <li>Legal and regulatory authorities when required by law</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rights" className="space-y-4">
          <Alert className="bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900/50 text-purple-800 dark:text-purple-300">
            <UserCheck className="h-4 w-4 text-purple-500" />
            <AlertTitle className="text-sm font-medium">
              Your Privacy Rights
            </AlertTitle>
            <AlertDescription className="text-xs text-purple-700 dark:text-purple-400">
              Understanding your rights regarding your personal information.
            </AlertDescription>
          </Alert>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <UserCheck className="h-4 w-4 mr-2 text-purple-500" />
                Your Rights Under Australian Privacy Law
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 dark:text-gray-400">
                Under the Australian Privacy Act 1988, you have the following
                rights
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      1
                    </Badge>
                    Access
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Request access to the personal information we hold about
                    you.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      2
                    </Badge>
                    Correction
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Request that we correct any inaccurate or incomplete
                    information.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      3
                    </Badge>
                    Deletion
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Request deletion of your personal information in certain
                    circumstances.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      4
                    </Badge>
                    Objection
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Object to the processing of your personal information in
                    certain circumstances.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      5
                    </Badge>
                    Complaint
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Lodge a complaint with the Office of the Australian
                    Information Commissioner (OAIC).
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-purple-100 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
                      6
                    </Badge>
                    Portability
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Request a copy of your personal data in a structured,
                    machine-readable format.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Email Communication Rights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 pl-5 list-disc">
                  <li>
                    Manage your email preferences through our Email Preferences
                    Center
                  </li>
                  <li>
                    Unsubscribe from any or all email communications at any time
                  </li>
                  <li>
                    Request a record of your consent history and email
                    preferences
                  </li>
                  <li>
                    Lodge a complaint if you receive unwanted emails after
                    unsubscribing
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Data Access and Portability
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 dark:text-gray-300">
                <p>
                  You can request a copy of your personal data in a structured,
                  commonly used, and machine-readable format. We will provide
                  this information free of charge, except where requests are
                  manifestly unfounded or excessive.
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <Phone className="h-4 w-4 mr-2 text-purple-500" />
                How to Exercise Your Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                To exercise any of these rights, please contact us at:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-purple-500" />
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    privacy@numberz.au
                  </span>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-purple-500" />
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    1300 123 435
                  </span>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-purple-500" />
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    Unit 41/2 Fastline Rd, Truganina
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">
                We will respond to your request within 30 days. We may need to
                verify your identity before processing your request.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Alert className="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/50 text-red-800 dark:text-red-300">
            <Lock className="h-4 w-4 text-red-500" />
            <AlertTitle className="text-sm font-medium">
              Data Security
            </AlertTitle>
            <AlertDescription className="text-xs text-red-700 dark:text-red-400">
              How we protect your personal and financial information.
            </AlertDescription>
          </Alert>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center">
                <Lock className="h-4 w-4 mr-2 text-red-500" />
                Security Measures
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 dark:text-gray-400">
                We implement a variety of security measures to maintain the
                safety of your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-red-100 hover:bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      1
                    </Badge>
                    Encryption
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    All data transmitted between your browser and our servers is
                    encrypted using TLS/SSL technology.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-red-100 hover:bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      2
                    </Badge>
                    Secure Storage
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Your data is stored in secure cloud environments with
                    industry-standard protections.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-red-100 hover:bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      3
                    </Badge>
                    Access Controls
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    We restrict access to personal information to authorized
                    employees who need it to perform their job functions.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-red-100 hover:bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      4
                    </Badge>
                    Regular Audits
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    We conduct regular security audits and vulnerability
                    assessments.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    <Badge className="mr-2 bg-red-100 hover:bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      5
                    </Badge>
                    Employee Training
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    All staff receive regular training on data protection and
                    security practices.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Email Security
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 pl-5 list-disc">
                  <li>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Authentication:
                    </span>{" "}
                    We use SPF, DKIM, and DMARC to authenticate our emails and
                    prevent spoofing.
                  </li>
                  <li>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Encryption:
                    </span>{" "}
                    Sensitive information sent via email is encrypted or sent
                    via secure links.
                  </li>
                  <li>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Monitoring:
                    </span>{" "}
                    We monitor for suspicious email activity and potential
                    security breaches.
                  </li>
                  <li>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Secure Handling:
                    </span>{" "}
                    Our staff follow strict protocols for handling client
                    information in emails.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  Data Breach Procedures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  In the event of a data breach that affects your personal
                  information:
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 pl-5 list-disc">
                  <li>
                    We will notify you promptly if there is a high risk to your
                    rights and freedoms
                  </li>
                  <li>
                    We will notify the Office of the Australian Information
                    Commissioner (OAIC) as required by law
                  </li>
                  <li>
                    We will provide information about the breach and steps you
                    can take to protect yourself
                  </li>
                  <li>
                    We will take immediate action to address the breach and
                    prevent future occurrences
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                Third-Party Security
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 dark:text-gray-300">
              <p>
                We carefully select third-party service providers and require
                them to maintain appropriate security measures to protect your
                information. We regularly review their security practices to
                ensure compliance with our standards.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-10 pt-6 border-t border-border px-4 max-w-3xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <Lock className="h-5 w-5 mr-2 text-primary" />
              Privacy Commitment
            </h3>
            <p className="text-muted-foreground">
              We're committed to protecting your privacy and ensuring the
              security of your personal information.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary" />
              Related Documents
            </h3>
            <ul className="space-y-2">
              <li>
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary hover:underline flex items-center"
                  onClick={() => (window.location.href = "/email/terms")}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Email Terms of Service
                </Button>
              </li>
              <li>
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary hover:underline flex items-center"
                  onClick={() => (window.location.href = "/privacy")}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Main Privacy Policy
                </Button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <Mail className="h-5 w-5 mr-2 text-primary" />
              Manage Preferences
            </h3>
            <Button
              variant="default"
              onClick={() => (window.location.href = "/email/preferences")}
            >
              Email Preferences
            </Button>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border text-center">
          <p className="text-sm text-muted-foreground">
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-AU", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      </div>
    </EmailPageLayout>
  );
}
